import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkSession = useCallback(async () => {
    // Skip the auth probe on fully public routes — onboarding magic links,
    // form viewer, public intake forms. These pages render their own gates
    // and have no advisor chrome that depends on AuthContext.user.
    const path = typeof window !== "undefined" ? window.location.pathname : "";
    if (/^\/(onboarding|form|intake)\//.test(path)) {
      setUser(null);
      setLoading(false);
      return;
    }
    // 1. Try the current access cookie.
    try {
      const res = await axios.get(`${API}/api/auth/me`, { withCredentials: true });
      setUser(res.data);
      setLoading(false);
      return;
    } catch (err) {
      // 2. Wave 12.b — Silent refresh. If /me 401'd (or any auth failure)
      // try to mint a fresh access token from the refresh cookie before
      // giving up. This is what keeps a refreshed browser tab signed-in
      // for the lifetime of the refresh cookie (7 days).
      if (err?.response?.status === 401) {
        try {
          const refreshed = await axios.post(
            `${API}/api/auth/refresh`,
            {},
            { withCredentials: true },
          );
          setUser(refreshed.data);
          setLoading(false);
          return;
        } catch {
          // refresh also failed — fall through to dev / null
        }
      }
    }
    // 3. Dev auto-login fallback (preview hosts only).
    try {
      const devRes = await axios.get(`${API}/api/auth/dev-status`);
      if (devRes.data.dev_preview) {
        const sessionRes = await axios.get(`${API}/api/auth/dev-session`, { withCredentials: true });
        setUser(sessionRes.data);
        setLoading(false);
        return;
      }
    } catch {}
    setUser(null);
    setLoading(false);
  }, []);

  useEffect(() => {
    checkSession();
  }, [checkSession]);

  const login = async (email, password) => {
    const res = await axios.post(`${API}/api/auth/login`, { email, password }, { withCredentials: true });
    setUser(res.data);
    return res.data;
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/api/auth/logout`, {}, { withCredentials: true });
    } catch {
      // ignore
    }
    setUser(null);
  };

  const refreshSession = async () => {
    try {
      const res = await axios.post(`${API}/api/auth/refresh`, {}, { withCredentials: true });
      setUser(res.data);
      return true;
    } catch {
      setUser(null);
      return false;
    }
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, refreshSession, checkSession }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
